import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const GAMES = [
  {
    id: 'parrotsave',
    title: 'Parrot Save',
    subtitle: 'Dodge or Die',
    emoji: '🦜',
    color: '#ff2680',
    bg: 'rgba(255,38,128,0.08)',
    description: 'Steer your parrot through waves of neon enemies. How long can you survive?',
    tag: 'DODGE',
    available: true,
    path: '/games/save',
  },
  {
    id: 'parrotjump',
    title: 'Parrot Jump',
    subtitle: 'Reach the Sky',
    emoji: '🪶',
    color: '#00cfff',
    bg: 'rgba(0,207,255,0.08)',
    description: "Leap from platform to platform. One wrong flap and it's over.",
    tag: 'JUMP',
    available: true,
    path: '/games/jump',
  },
  {
    id: 'parrotcart',
    title: 'Parrot Cart',
    subtitle: 'Race the Flock',
    emoji: '🛒',
    color: '#ffd700',
    bg: 'rgba(255,215,0,0.08)',
    description: 'High-speed cart racing through the neon jungle. Overtake the flock.',
    tag: 'RACE',
    available: true,
    path: '/games/cart',
  },
  {
    id: 'parrotvsnakes',
    title: 'Parrot vs Snakes',
    subtitle: 'Feathers & Fangs',
    emoji: '🐍',
    color: '#00ff88',
    bg: 'rgba(0,255,136,0.08)',
    description: 'The classic feud. Eat or be eaten in this rapid-fire arena brawl.',
    tag: 'BATTLE',
    available: true,
    path: '/games/znakes',
  },
]

const MONO = "'JetBrains Mono', 'Courier New', monospace"
const PINK = '#ff2680'
const BG   = '#07000a'

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&display=swap');

  .gr { /* games-root */
    min-height: 100dvh;
    background: ${BG};
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: ${MONO};
    overflow-x: hidden;
    position: relative;
  }

  .gr::before {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 100;
    background-image: repeating-linear-gradient(
      0deg, transparent, transparent 2px,
      rgba(0,0,0,0.055) 2px, rgba(0,0,0,0.055) 4px
    );
  }

  .gr-bg {
    position: fixed; inset: 0;
    pointer-events: none; z-index: 0; overflow: hidden;
  }
  .gr-bg::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(255,38,128,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,38,128,0.035) 1px, transparent 1px);
    background-size: 56px 56px;
  }
  .gr-bg::after {
    content: '';
    position: absolute;
    top: -5%; left: 50%; transform: translateX(-50%);
    width: min(900px, 130vw); height: 500px;
    background: radial-gradient(ellipse, rgba(255,38,128,0.08) 0%, transparent 65%);
  }

  /* ── Top bar ── */
  .gr-topbar {
    position: relative; z-index: 10;
    width: 100%; max-width: 640px;
    padding: clamp(14px, 4vw, 22px) clamp(16px, 5vw, 24px) 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .gr-brand {
    font-size: clamp(7px, 2vw, 9px);
    letter-spacing: clamp(2px, 1vw, 4px);
    color: rgba(255,38,128,0.38);
    text-transform: uppercase;
    white-space: nowrap;
  }

  .lb-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: clamp(6px, 2vw, 9px) clamp(10px, 3vw, 16px);
    border-radius: 8px;
    border: 1px solid rgba(255,38,128,0.22);
    background: rgba(255,38,128,0.055);
    color: rgba(255,38,128,0.55);
    font-family: ${MONO};
    font-size: clamp(7px, 2vw, 9px);
    letter-spacing: clamp(1px, 0.5vw, 2.5px);
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    flex-shrink: 0;
    outline: none;
  }
  .lb-btn:hover {
    border-color: ${PINK};
    background: rgba(255,38,128,0.11);
    color: ${PINK};
    box-shadow: 0 0 18px rgba(255,38,128,0.18);
  }
  .lb-btn:active { transform: scale(0.95); }
  .lb-icon { font-size: clamp(11px, 3vw, 14px); line-height: 1; }

  @media (max-width: 340px) {
    .lb-label { display: none; }
    .lb-btn { padding: 7px 9px; }
  }

  /* ── Content ── */
  .gr-content {
    position: relative; z-index: 1;
    width: 100%; max-width: 640px;
    padding: clamp(24px, 6vw, 40px) clamp(16px, 5vw, 24px) clamp(40px, 8vw, 64px);
    display: flex; flex-direction: column;
  }

  /* ── Header ── */
  .gr-header {
    text-align: center;
    margin-bottom: clamp(24px, 6vw, 40px);
  }
  .gr-title {
    font-size: clamp(44px, 14vw, 76px);
    font-family: ${MONO};
    font-weight: 800;
    letter-spacing: clamp(3px, 2vw, 10px);
    background: linear-gradient(135deg, #ff2680 0%, #ff6eb4 42%, #ffd700 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    margin-bottom: 10px;
  }
  .gr-sub {
    font-size: clamp(7px, 2vw, 9px);
    letter-spacing: clamp(3px, 1.5vw, 5px);
    color: rgba(255,255,255,0.14);
  }

  /* ── List ── */
  .gr-list {
    display: flex; flex-direction: column;
    gap: clamp(6px, 2vw, 10px);
  }

  /* ── Card ── */
  .gc {
    display: flex;
    align-items: center;
    gap: clamp(12px, 3.5vw, 18px);
    padding: clamp(13px, 3.5vw, 18px) clamp(14px, 4vw, 20px);
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.055);
    background: rgba(255,255,255,0.018);
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
  .gc.hov  { transform: scale(1.013); }
  .gc.prs  { transform: scale(0.972); }
  .gc.off  { opacity: 0.38; cursor: default; }

  .gc-emoji {
    font-size: clamp(22px, 6vw, 30px);
    width: clamp(42px, 11vw, 52px);
    height: clamp(42px, 11vw, 52px);
    display: flex; align-items: center; justify-content: center;
    border-radius: 8px;
    flex-shrink: 0;
    transition: filter 0.2s;
  }

  .gc-body { flex: 1; min-width: 0; }

  .gc-title-row {
    display: flex; align-items: center;
    gap: 7px; margin-bottom: 4px;
    flex-wrap: wrap;
  }
  .gc-title {
    font-size: clamp(11px, 3.5vw, 14px);
    font-weight: 700;
    letter-spacing: 0.5px;
    transition: color 0.15s;
    white-space: nowrap;
  }
  .gc-tag {
    font-size: clamp(6px, 1.8vw, 7.5px);
    letter-spacing: 1.5px;
    border-radius: 3px;
    padding: 2px 5px;
    flex-shrink: 0;
  }
  .gc-desc {
    font-size: clamp(8px, 2.2vw, 10px);
    color: rgba(255,255,255,0.26);
    letter-spacing: 0.15px;
    line-height: 1.65;
  }

  .gc-arrow {
    font-size: clamp(14px, 4vw, 18px);
    transition: color 0.15s, transform 0.15s;
    flex-shrink: 0;
  }
  .gc.hov .gc-arrow { transform: translateX(4px); }

  /* ── Soon / Footer ── */
  .gr-soon {
    margin-top: 8px;
    padding: clamp(10px, 3vw, 13px) 18px;
    border: 1px dashed rgba(255,38,128,0.1);
    border-radius: 8px;
    text-align: center;
    color: rgba(255,255,255,0.1);
    font-size: clamp(7px, 1.8vw, 8px);
    letter-spacing: 3px;
  }

  .gr-footer {
    margin-top: clamp(28px, 7vw, 44px);
    display: flex; justify-content: center; align-items: center;
    gap: clamp(14px, 4vw, 24px);
    flex-wrap: wrap;
  }
  .gr-footer a {
    font-size: clamp(7px, 1.8vw, 8px);
    color: rgba(255,38,128,0.26);
    letter-spacing: 2px;
    text-decoration: none;
    text-transform: uppercase;
    transition: color 0.15s;
  }
  .gr-footer a:hover { color: ${PINK}; }
  .gr-sep { color: rgba(255,38,128,0.1); font-size: 8px; }

  /* ── Animations ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .au {
    opacity: 0;
    animation: fadeUp 0.38s ease forwards;
  }
`

function GameCard({
  game,
  onTap,
  delay,
}: {
  game: typeof GAMES[0]
  onTap: () => void
  delay: number
}) {
  const [hov, setHov] = useState(false)
  const [prs, setPrs] = useState(false)

  const cls = [
    'gc au',
    hov && game.available ? 'hov' : '',
    prs && game.available ? 'prs' : '',
    !game.available ? 'off' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={cls}
      style={{
        animationDelay: `${delay}ms`,
        border: `1px solid ${hov && game.available ? game.color + '50' : 'rgba(255,255,255,0.055)'}`,
        background: hov && game.available ? game.bg : 'rgba(255,255,255,0.018)',
        boxShadow: hov && game.available
          ? `0 0 28px ${game.color}22, inset 0 0 24px ${game.color}06`
          : '0 2px 12px rgba(0,0,0,0.35)',
      }}
      onClick={game.available ? onTap : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setHov(false); setPrs(false) }}
      onMouseDown={() => game.available && setPrs(true)}
      onMouseUp={() => setPrs(false)}
      onTouchStart={() => game.available && setPrs(true)}
      onTouchEnd={() => { setPrs(false); if (game.available) onTap() }}
    >
      <div
        className="gc-emoji"
        style={{
          background: `${game.color}13`,
          border: `1px solid ${game.color}26`,
          filter: hov && game.available ? `drop-shadow(0 0 10px ${game.color}99)` : 'none',
        }}
      >
        {game.emoji}
      </div>

      <div className="gc-body">
        <div className="gc-title-row">
          <span className="gc-title" style={{ color: hov && game.available ? game.color : '#fff' }}>
            {game.title}
          </span>
          <span
            className="gc-tag"
            style={{
              color: game.available ? game.color : 'rgba(255,255,255,0.22)',
              border: `1px solid ${game.available ? game.color + '40' : 'rgba(255,255,255,0.1)'}`,
              background: game.available ? `${game.color}0c` : 'transparent',
            }}
          >
            {game.available ? game.tag : 'SOON'}
          </span>
        </div>
        <div className="gc-desc">{game.description}</div>
      </div>

      {game.available && (
        <div className="gc-arrow" style={{ color: hov ? game.color : 'rgba(255,255,255,0.09)' }}>
          →
        </div>
      )}
    </div>
  )
}

export default function Games() {
  const navigate = useNavigate()

  useEffect(() => {
    const id = 'gp-css'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id
    el.textContent = CSS
    document.head.appendChild(el)
    return () => { document.getElementById(id)?.remove() }
  }, [])

  return (
    <div className="gr">
      <div className="gr-bg" />

      {/* Top bar with leaderboard button */}
      <div className="gr-topbar au" style={{ animationDelay: '0ms' }}>
        <span className="gr-brand">The 10K Squad</span>
        <button className="lb-btn" onClick={() => navigate('/games/scores')}>
          <span className="lb-icon">🏆</span>
          <span className="lb-label">Leaderboard</span>
        </button>
      </div>

      <div className="gr-content">
        {/* Header */}
        <div className="gr-header au" style={{ animationDelay: '50ms' }}>
          <div className="gr-title">ARCADE</div>
          <div className="gr-sub">Select your game</div>
        </div>

        {/* Cards */}
        <div className="gr-list">
          {GAMES.map((game, i) => (
            <GameCard
              key={game.id}
              game={game}
              onTap={() => navigate(game.path)}
              delay={80 + i * 65}
            />
          ))}
        </div>

        {/* Coming soon */}
        <div className="gr-soon au" style={{ animationDelay: `${80 + GAMES.length * 65 + 40}ms` }}>
          ✦ &nbsp; More games dropping soon &nbsp; ✦
        </div>

        {/* Footer */}
        <div className="gr-footer au" style={{ animationDelay: `${80 + GAMES.length * 65 + 100}ms` }}>
          <a href="https://opensea.io/collection/the-10k-squad-350905768" target="_blank" rel="noopener noreferrer">
            OpenSea
          </a>
          <span className="gr-sep">·</span>
          <a href="https://x.com/10ksquadbot" target="_blank" rel="noopener noreferrer">
            @10ksquadbot
          </a>
        </div>
      </div>
    </div>
  )
}